/**
 * How the logical 320×200 frame reaches the screen (AD-009's presentation
 * rule): nearest-neighbour, an *integer* horizontal scale, and the VGA
 * mode-13h pixel aspect of 1:1.2 so 320×200 shows as 4:3 rather than 8:5.
 * Vertical scale is therefore `1.2 × scale` and deliberately non-integer —
 * duplicating some rows is what makes the circles round, and it is exactly
 * what a CRT did. Everything left over is letterbox.
 */

import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from "./draw-ops.ts";

/** Mode 13h pixels are 20% taller than they are wide. */
export const PIXEL_ASPECT = 1.2;

/** Device-pixel size of the visible canvas. Named for the shell so it does not collide with the map viewer's own `Viewport`. */
export interface ShellViewport {
  readonly width: number;
  readonly height: number;
}

export interface Presentation {
  /** Integer horizontal magnification; 1 when the viewport is smaller than one whole frame. */
  readonly scale: number;
  readonly destWidth: number;
  readonly destHeight: number;
  readonly originX: number;
  readonly originY: number;
}

export function computePresentation(viewport: ShellViewport): Presentation {
  const fitX = Math.floor(viewport.width / LOGICAL_WIDTH);
  const fitY = Math.floor(viewport.height / (LOGICAL_HEIGHT * PIXEL_ASPECT));
  const scale = Math.max(1, Math.min(fitX, fitY));
  const destWidth = LOGICAL_WIDTH * scale;
  const destHeight = Math.round(LOGICAL_HEIGHT * PIXEL_ASPECT * scale);
  return {
    scale,
    destWidth,
    destHeight,
    originX: Math.floor((viewport.width - destWidth) / 2),
    originY: Math.floor((viewport.height - destHeight) / 2),
  };
}

export interface LogicalPoint {
  readonly x: number;
  readonly y: number;
}

/** An element's rendered box, as `getBoundingClientRect` gives it. */
export interface ElementBounds {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** A canvas's backing store, in device pixels. */
export interface BackingSize {
  readonly width: number;
  readonly height: number;
}

/**
 * The backing store a shell canvas of this CSS box wants, in device pixels.
 *
 * Sizing the buffer to the device makes the integer scale an integer count of
 * *real* pixels. **What that is worth depends entirely on whether the ratio is
 * a whole number, and the measurement is worth writing down because the obvious
 * claim for it is wrong:**
 *
 * - At `dpr: 2`, it changes **nothing**. `.shell__screen` carries
 *   `image-rendering: pixelated`, so the compositor's upscale is itself
 *   nearest-neighbour, and a scale-3 blit doubled by the compositor is the same
 *   set of pixels as a scale-6 blit. Measured over `/missions` at 1280x800:
 *   **10,562 px of 4,096,000 differ, 0.26 %**, and that residue is the 3D inset,
 *   not the menu.
 * - At `dpr: 1.5` it is a real repair. The old path scaled 320 up by an integer
 *   in *CSS* pixels and then let the compositor stretch that by 1.5, which
 *   nearest-neighbour cannot do evenly — each logical pixel landed on four
 *   device pixels or five, so letter stems came out different widths within a
 *   single word. Choosing the scale in device pixels instead gives every logical
 *   pixel exactly five. Measured at 1280x800: **428,858 px of 2,304,000 differ,
 *   18.6 %**, and the picture is both larger (1600 device px wide against 1440)
 *   and uniform.
 *
 * So this is a fractional-density fix, and on the common 1x and 2x displays it
 * is deliberately a no-op. The painted cockpit panel is the opposite case and
 * gains at every ratio — `.cockpit-panel` has no `image-rendering`, so its
 * compositor upscale smooths.
 */
export function shellBackingSize(clientWidth: number, clientHeight: number, devicePixelRatio: number): BackingSize {
  return {
    width: Math.max(1, Math.floor(clientWidth * devicePixelRatio)),
    height: Math.max(1, Math.floor(clientHeight * devicePixelRatio)),
  };
}

/**
 * A pointer's position in the canvas's own device pixels.
 *
 * A pointer event is in CSS pixels and the presentation is in device pixels, so
 * the offset into the element is scaled by the element's own ratio. Derived
 * from the **rendered** box rather than from `devicePixelRatio` directly, so a
 * CSS transform on an ancestor stays correct, and so a click cannot be mapped
 * through a density the canvas has not been resized to yet.
 */
export function toViewPixels(
  bounds: ElementBounds,
  backing: BackingSize,
  clientX: number,
  clientY: number,
): { readonly x: number; readonly y: number } {
  const ratioX = bounds.width === 0 ? 1 : backing.width / bounds.width;
  const ratioY = bounds.height === 0 ? 1 : backing.height / bounds.height;
  return { x: (clientX - bounds.left) * ratioX, y: (clientY - bounds.top) * ratioY };
}

/** Inverse of the presentation transform. `undefined` for a point in the letterbox. */
export function toLogical(presentation: Presentation, viewX: number, viewY: number): LogicalPoint | undefined {
  const localX = viewX - presentation.originX;
  const localY = viewY - presentation.originY;
  if (localX < 0 || localY < 0 || localX >= presentation.destWidth || localY >= presentation.destHeight) return undefined;
  return {
    x: Math.min(LOGICAL_WIDTH - 1, Math.floor((localX * LOGICAL_WIDTH) / presentation.destWidth)),
    y: Math.min(LOGICAL_HEIGHT - 1, Math.floor((localY * LOGICAL_HEIGHT) / presentation.destHeight)),
  };
}
